/**
 * The bridge between a trigger's stored condition and the shared filter model
 * (`@/shared/components/filters`): which fields the condition cards offer for
 * the subscribed events, and how a stored condition tree projects into flat
 * filter rows and back.
 */

import type {
  FilterDefinition,
  FilterOptions,
  FilterOptionsResolver,
} from "@/shared/components/filters";
import {
  comparatorsByType,
  isValueless,
  type Filter,
  type FilterComparator,
  type FilterMatchMode,
} from "@/shared/domain/query/filters";
import {
  EVENT_CATALOG,
  templateTypeOf,
  variableRoots,
  type PlatformEventType,
  type TemplatePathType,
  type TriggerTemplateCatalog,
} from "@/shared/domain/triggers";
import type { Condition, ConditionGroup, SimpleCondition } from "@/types";

/** Org-specific vocabulary the catalog's samples cannot supply. */
export interface ConditionVocabulary {
  /**
   * Metadata keys per entity root (``dataset``, ``file``, ``session``): the
   * ``<root>.metadata.<key>`` fields offered, and the ``changed.<key>`` fields
   * on metadata-update events.
   */
  metadataKeys: Readonly<Record<string, readonly string[]>>;
  /** Tag option sources per entity root; ``tag.added``/``tag.removed`` use the first entity root's. */
  tagOptions: Readonly<Record<string, FilterOptions>>;
}

/** Roots that are not entities: their children are per-event, not fields to offer. */
const NON_ENTITY_ROOTS: ReadonlySet<string> = new Set(["changed", "tag"]);

/** Every root any event exposes, for telling a qualified field from a bare one. */
const ALL_ROOTS: ReadonlySet<string> = new Set(
  Object.values(EVENT_CATALOG).flatMap((descriptor) => descriptor.roots),
);

/** Path segments under an entity whose sample children are noise, not schema. */
const OPAQUE_SEGMENTS: ReadonlySet<string> = new Set([
  "metadata",
  "custom_fields",
  "storage_ctx",
]);

/**
 * An option source that offers whatever the author typed: for list fields with
 * no directory behind them (input patterns, event names). Reports ``truncated``
 * so the picker keeps asking rather than freezing an empty first page.
 */
export const freeTextOptions: FilterOptionsResolver = (search) =>
  Promise.resolve({
    options: search.length > 0 ? [search] : [],
    truncated: true,
  });

/**
 * Wrap a server tag search so the typed term is always offered as well: a
 * trigger may legitimately name a tag nothing carries yet.
 */
export function tagOptionsWithFreeText(
  search: FilterOptionsResolver,
): FilterOptionsResolver {
  return async (term, signal) => {
    const resolved = await search(term, signal);
    const page = Array.isArray(resolved)
      ? { options: resolved as string[], truncated: false }
      : resolved;
    const options = (page.options as Array<string | { value: string }>).map(
      (option) => (typeof option === "string" ? option : option.value),
    );
    const trimmed = term.trim();
    if (trimmed.length > 0 && !options.includes(trimmed)) {
      options.unshift(trimmed);
    }
    return { options, truncated: page.truncated };
  };
}

/** The filter row a path's data type calls for, or null for one with no row. */
function scalarDefinition(
  field: string,
  type: TemplatePathType | null,
): FilterDefinition | null {
  switch (type) {
    case "number":
      return { type: "numeric", field, name: field, nullable: true };
    case "boolean":
      return { type: "boolean", field, name: field, nullable: true };
    case "date":
      return {
        type: "date",
        field,
        name: field,
        nullable: true,
        withoutPresets: true,
      };
    case "string":
      return { type: "string", field, name: field, nullable: true };
    case "set":
      return { type: "set", field, name: field, options: freeTextOptions };
    default:
      return null;
  }
}

/**
 * The fields the condition builder offers for a trigger subscribed to
 * ``events``: one per scalar path the catalog exposes under a root every
 * platform event shares, plus the envelope (its type, source, subject), with
 * metadata keys and tags drawn from the org rather than the sample. The
 * trigger root is omitted: a condition on the trigger's own fields never
 * narrows anything.
 */
export function triggerConditionDefinitions(
  catalog: TriggerTemplateCatalog,
  events: readonly PlatformEventType[],
  vocabulary: ConditionVocabulary,
): FilterDefinition[] {
  const roots =
    events.length === 0 ? [] : [...variableRoots(events), "envelope"];
  const entityRoots = roots.filter((root) => !NON_ENTITY_ROOTS.has(root));
  const primaryRoot = entityRoots[0];
  const tagOptionsFor = (root: string): FilterOptions =>
    vocabulary.tagOptions[root] ??
    (primaryRoot !== undefined ? vocabulary.tagOptions[primaryRoot] : null) ??
    freeTextOptions;

  const definitions: FilterDefinition[] = [];
  for (const entry of catalog.paths) {
    if (!roots.includes(entry.root) || NON_ENTITY_ROOTS.has(entry.root)) {
      continue;
    }
    const segments = entry.path.split(".");
    if (segments.length > 2 && OPAQUE_SEGMENTS.has(segments[1])) {
      continue;
    }
    if (entry.path === `${entry.root}.metadata`) {
      for (const key of vocabulary.metadataKeys[entry.root] ?? []) {
        const field = `${entry.root}.metadata.${key}`;
        definitions.push({
          type: "string",
          field,
          name: field,
          nullable: true,
        });
      }
      continue;
    }
    if (entry.container) {
      continue;
    }
    if (entry.path === `${entry.root}.tags`) {
      definitions.push({
        type: "set",
        field: entry.path,
        name: entry.path,
        options: tagOptionsFor(entry.root),
      });
      continue;
    }
    const definition = scalarDefinition(entry.path, entry.type);
    if (definition !== null) {
      definitions.push(definition);
    }
  }

  if (roots.includes("changed")) {
    const keys = new Set(
      entityRoots.flatMap((root) => vocabulary.metadataKeys[root] ?? []),
    );
    for (const key of keys) {
      const field = `changed.${key}`;
      definitions.push({ type: "string", field, name: field, nullable: true });
    }
  }
  if (roots.includes("tag")) {
    for (const field of ["tag.added", "tag.removed"]) {
      definitions.push({
        type: "set",
        field,
        name: field,
        options:
          primaryRoot !== undefined
            ? tagOptionsFor(primaryRoot)
            : freeTextOptions,
      });
    }
  }
  return definitions;
}

/**
 * Condition fields whose explicit root is not shared by every subscribed
 * event. Bare fields bind to the default root, which is always shared.
 */
export function conditionRootViolations(
  condition: Condition | null,
  sharedRoots: readonly string[],
): string[] {
  const violations: string[] = [];
  for (const leaf of leaves(condition)) {
    const root = leaf.field.split(".", 1)[0];
    if (ALL_ROOTS.has(root) && !sharedRoots.includes(root)) {
      violations.push(leaf.field);
    }
  }
  return violations;
}

/** How many leaf clauses ``condition`` holds; 0 for none. */
export function countClauses(condition: Condition | null): number {
  let count = 0;
  for (const _leaf of leaves(condition)) {
    count += 1;
  }
  return count;
}

function isGroup(condition: Condition): condition is ConditionGroup {
  return "operator" in condition;
}

function* leaves(condition: Condition | null): Generator<SimpleCondition> {
  if (condition === null) {
    return;
  }
  if (isGroup(condition)) {
    for (const child of condition.conditions) {
      yield* leaves(child);
    }
  } else {
    yield condition;
  }
}

/** What a stored condition looks like as builder rows. */
export interface ConditionFilters {
  filters: Filter[];
  matchMode: FilterMatchMode;
  /**
   * Definitions invented for fields the offered vocabulary lacks (a metadata
   * key the org list no longer has, a bare legacy field), so their chips
   * still render and edit.
   */
  extraDefinitions: FilterDefinition[];
}

const NEGATIVE_MULTI_VALUE: ReadonlySet<string> = new Set([
  "NOT_EQUALS",
  "NOT_CONTAINS",
  "NOT_LIKE",
]);

/**
 * Project a stored condition into the builder's flat rows, recognizing the
 * shapes `filterToCondition` emits (a multi-value row as an OR/AND of one
 * field, a date BETWEEN as GTE+LTE) so a saved trigger reopens as it was
 * built. ``null`` when the tree is something the builder cannot represent —
 * nesting, NOT, a comparator the filter vocabulary lacks — and the caller
 * should show it read-only and preserve it as-is.
 */
export function conditionToFilters(
  condition: Condition | null,
  definitions: readonly FilterDefinition[],
): ConditionFilters | null {
  const extra: FilterDefinition[] = [];
  const lookup = (field: string, sample: unknown): FilterDefinition => {
    const known =
      definitions.find((definition) => definition.field === field) ??
      extra.find((definition) => definition.field === field);
    if (known !== undefined) {
      return known;
    }
    const synthesized = synthesizeDefinition(field, sample);
    extra.push(synthesized);
    return synthesized;
  };

  if (condition === null) {
    return { filters: [], matchMode: "AND", extraDefinitions: [] };
  }
  if (!isGroup(condition)) {
    const filter = leafToFilter(condition, lookup);
    return filter === null
      ? null
      : { filters: [filter], matchMode: "AND", extraDefinitions: extra };
  }
  if (condition.operator === "NOT") {
    return null;
  }
  const compound = compoundToFilter(condition, lookup);
  if (compound !== null) {
    return { filters: [compound], matchMode: "AND", extraDefinitions: extra };
  }
  const filters: Filter[] = [];
  for (const child of condition.conditions) {
    let filter: Filter | null;
    if (!isGroup(child)) {
      filter = leafToFilter(child, lookup);
    } else if (child.operator === "NOT") {
      filter = null;
    } else {
      filter = compoundToFilter(child, lookup);
    }
    if (filter === null) {
      return null;
    }
    filters.push(filter);
  }
  return { filters, matchMode: condition.operator, extraDefinitions: extra };
}

function synthesizeDefinition(
  field: string,
  sample: unknown,
): FilterDefinition {
  if (field.endsWith(".tags") || field === "tags") {
    return { type: "set", field, name: field, options: freeTextOptions };
  }
  // A stored condition names a field the offered vocabulary lacks, so its own
  // value is the only statement of the type.
  return (
    scalarDefinition(field, templateTypeOf(sample)) ?? {
      type: "string",
      field,
      name: field,
      nullable: true,
    }
  );
}

type Lookup = (field: string, sample: unknown) => FilterDefinition;

function comparatorAllowed(
  definition: FilterDefinition,
  comparator: string,
): comparator is FilterComparator {
  return (comparatorsByType[definition.type] as readonly string[]).includes(
    comparator,
  );
}

function leafToFilter(leaf: SimpleCondition, lookup: Lookup): Filter | null {
  const definition = lookup(leaf.field, leaf.value);
  if (!comparatorAllowed(definition, leaf.comparator)) {
    return null;
  }
  return rowFor(
    definition,
    leaf.comparator,
    isValueless(leaf.comparator) ? [] : [leaf.value],
  );
}

/**
 * Assemble one filter row from a definition, a comparator, and raw wire
 * values, coercing each value to the row's type. ``null`` when a value cannot
 * be coerced (a non-numeric string on a numeric field).
 */
function rowFor(
  definition: FilterDefinition,
  comparator: FilterComparator,
  values: readonly unknown[],
): Filter | null {
  const field = definition.field;
  switch (definition.type) {
    case "numeric": {
      const numbers = values.map(Number);
      if (numbers.some((n) => !Number.isFinite(n))) {
        return null;
      }
      return { type: "numeric", field, comparator, values: numbers } as Filter;
    }
    case "boolean":
      return {
        type: "boolean",
        field,
        comparator,
        values: values.map((v) => v === true || v === "true"),
      } as Filter;
    case "date":
      return {
        type: "date",
        field,
        comparator,
        values: values.map(String),
      } as Filter;
    case "set":
    case "enum":
      return {
        type: definition.type,
        field,
        comparator,
        values: values.map((v) => ({ value: String(v), label: String(v) })),
      } as Filter;
    case "string":
      return {
        type: "string",
        field,
        comparator,
        values: values.map(String),
      } as Filter;
    default:
      return null;
  }
}

/**
 * A group that is really one row: every child a leaf on the same field with
 * the same comparator, joined the way `filterToCondition` joins multiple
 * values (OR for positive comparators, AND for negative ones); or the
 * GTE+LTE pair it emits for a date BETWEEN.
 */
function compoundToFilter(
  group: ConditionGroup,
  lookup: Lookup,
): Filter | null {
  const children = group.conditions;
  if (children.length < 2 || children.some(isGroup)) {
    return null;
  }
  const leafChildren = children as SimpleCondition[];
  const field = leafChildren[0].field;
  if (leafChildren.some((leaf) => leaf.field !== field)) {
    return null;
  }
  const definition = lookup(field, leafChildren[0].value);

  if (
    definition.type === "date" &&
    group.operator === "AND" &&
    leafChildren.length === 2 &&
    leafChildren[0].comparator === "GREATER_THAN_OR_EQUAL" &&
    leafChildren[1].comparator === "LESS_THAN_OR_EQUAL"
  ) {
    return rowFor(definition, "BETWEEN", [
      leafChildren[0].value,
      leafChildren[1].value,
    ]);
  }

  const comparator = leafChildren[0].comparator;
  if (leafChildren.some((leaf) => leaf.comparator !== comparator)) {
    return null;
  }
  const expectedOperator = NEGATIVE_MULTI_VALUE.has(comparator) ? "AND" : "OR";
  if (group.operator !== expectedOperator) {
    return null;
  }
  if (!comparatorAllowed(definition, comparator) || isValueless(comparator)) {
    return null;
  }
  return rowFor(
    definition,
    comparator,
    leafChildren.map((leaf) => leaf.value),
  );
}
